import { useEffect, useRef, useState } from 'react';
import type { Rating, Visibility } from '../types';

/**
 * 「ポーズの表示設定」（公開範囲・閲覧制限）を変更するモーダル。
 * UsageTermsModal と同じ作法（draft を「変更する」で確定、それまで外部 state は変えない）。
 */
interface DisplaySettingsModalProps {
  open: boolean;
  visibility: Visibility;
  rating: Rating;
  onCancel: () => void;
  onConfirm: (next: { visibility: Visibility; rating: Rating }) => void;
}

export default function DisplaySettingsModal({
  open,
  visibility,
  rating,
  onCancel,
  onConfirm,
}: DisplaySettingsModalProps) {
  const [draftVisibility, setDraftVisibility] = useState<Visibility>(visibility);
  const [draftRating, setDraftRating] = useState<Rating>(rating);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setDraftVisibility(visibility);
      setDraftRating(rating);
    }
  }, [open, visibility, rating]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="display-settings-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="display-settings-title" className="modal__title">
            表示設定を変更
          </h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">
            ×
          </button>
        </div>

        <div className="field">
          <label>公開範囲</label>
          <div className="radio-group">
            <label className="radio">
              <input
                type="radio"
                name="display-visibility"
                checked={draftVisibility === 'public'}
                onChange={() => setDraftVisibility('public')}
              />
              公開 — みんなの投稿一覧・検索結果に表示されます
            </label>
            <label className="radio">
              <input
                type="radio"
                name="display-visibility"
                checked={draftVisibility === 'private'}
                onChange={() => setDraftVisibility('private')}
              />
              非公開 — 自分だけが閲覧できます
            </label>
          </div>
        </div>

        <div className="field">
          <label>閲覧制限</label>
          <div className="radio-group">
            <label className="radio">
              <input
                type="radio"
                name="display-rating"
                checked={draftRating === 'all'}
                onChange={() => setDraftRating('all')}
              />
              全年齢
            </label>
            <label className="radio">
              <input
                type="radio"
                name="display-rating"
                checked={draftRating === 'sensitive'}
                onChange={() => setDraftRating('sensitive')}
              />
              センシティブ — 刺激の強い表現を含む場合に選択してください
            </label>
          </div>
        </div>

        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onConfirm({ visibility: draftVisibility, rating: draftRating })}
            ref={confirmBtnRef}
          >
            変更する
          </button>
        </div>
      </div>
    </div>
  );
}
